import clsx from "clsx";
import { FC, MouseEvent } from "react";

import { useAssessmentMode } from "../../../../../utils/assessment";

import { AssessmentStatus } from "api/api-data-types";
import { getAssessmentStatusLabel } from "components/materials/section/assessment/utils";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { TrackedDiv } from "mds/components/TrackedDiv";
import { CircleInfoIcon } from "mds/icons";
import { useSectionCapabilitiesBySectionId } from "store/capability-hooks";

export type AssessmentStatusChipProps = {
  page: PageType;
  assessmentStatus?: AssessmentStatus;
  unassessedLateCount?: number;
  assessedSubmissionsCount?: number;
};

export const AssessmentStatusChip: FC<AssessmentStatusChipProps> = ({
  page,
  assessmentStatus,
  unassessedLateCount,
  assessedSubmissionsCount,
}) => {
  const { startAssessing } = useAssessmentMode(page);
  const { canAuthorSection } = useSectionCapabilitiesBySectionId(page.section_id);

  // Shouldn't happen but we want to be safe
  if (assessmentStatus === AssessmentStatus.NOT_ASSESSABLE || !assessmentStatus) {
    return null;
  }

  const canAssess = assessmentStatus === AssessmentStatus.ASSESSABLE;

  let extraClasses = "cursor-default text-green-shade-20 bg-green-tint-90 hover:text-green";
  let role = "";
  let onClick: (e: MouseEvent) => void = () => {};

  if (canAssess) {
    // Make the div button look green and clickable.
    if (page.assessing_marked_complete_at) {
      extraClasses += " cursor-pointer";
    } else {
      extraClasses =
        "cursor-pointer text-white bg-blue-shade-20 hover:bg-blue hover:text-white active:bg-blue-shade-40";
    }

    role = "button";

    onClick = (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      startAssessing();
    };
  }

  const statusText = getAssessmentStatusLabel(
    !!page.assessing_marked_complete_at,
    assessedSubmissionsCount || 0,
    assessmentStatus === AssessmentStatus.ASSESSED,
  );

  // Note that we hide the Tooltip for students for now, but may
  // want to show a different message to students in the future.
  return (
    <TrackedDiv
      className={clsx(
        "list-item-chip flex items-center justify-center gap-1 rounded-md px-2 py-1 text-xs font-medium",
        extraClasses,
      )}
      eventName="Assessment - Start"
      eventProps={{ pageTitle: page.title, pageId: page.id }}
      role={role}
      onClick={canAssess ? onClick : undefined}
    >
      <span>{statusText}</span>
      {!!unassessedLateCount && unassessedLateCount > 0 && canAuthorSection && (
        <Tooltip text={t("assessment.late_assessment_count", { count: unassessedLateCount })}>
          <CircleInfoIcon />
        </Tooltip>
      )}
    </TrackedDiv>
  );
};
